import { getString } from 'firebase/remote-config';
import { remoteConfig } from './firebase';

export interface RaceSearchEvent {
  title: string;
  location?: string;
  distances: string[];
  startDate?: string;
}

interface PageInfo {
  totalResults: number;
  first: string;
  last: string;
  prev: string;
  next: string;
}

interface EventSearchResponse {
  data: RaceSearchEvent[];
  page: PageInfo;
}

const BASE_URL = 'https://api.letsdothis.com/marketplace/v1';

const cache = new Map<string, { data: EventSearchResponse; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const getApiToken = (): string => {
  try {
    return getString(remoteConfig, 'lets_do_this_api_key') || '';
  } catch {
    console.error('[EventsService] Failed to get API token from Remote Config');
    return '';
  }
};

export const searchRaceEvents = async (
  query: string,
  options?: { searchAfter?: string; pageSize?: number }
): Promise<{ events: RaceSearchEvent[]; nextCursor: string; hasMore: boolean }> => {
  if (query.length < 3) {
    return { events: [], nextCursor: '', hasMore: false };
  }

  const pageSize = options?.pageSize ?? 20;
  const searchAfter = options?.searchAfter ?? '';

  const cacheKey = `search_${query}_${pageSize}_${searchAfter}`;
  const cached = cache.get(cacheKey);
  if (cached && !searchAfter && Date.now() - cached.timestamp < CACHE_TTL) {
    const response = cached.data;
    return {
      events: response.data,
      nextCursor: response.page.next,
      hasMore: response.page.next !== '',
    };
  }

  const token = getApiToken();
  if (!token) {
    throw new Error('Events API token not available');
  }

  const params = new URLSearchParams({
    query,
    discipline: 'Running',
    pageSize: String(pageSize),
  });
  if (searchAfter) {
    params.set('searchAfter', searchAfter);
  }

  const response = await fetch(`${BASE_URL}/events/search?${params}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Events search failed: ${response.status}`);
  }

  const data: EventSearchResponse = await response.json();

  if (!searchAfter) {
    cache.set(cacheKey, { data, timestamp: Date.now() });
  }

  return {
    events: data.data,
    nextCursor: data.page.next,
    hasMore: data.page.next !== '',
  };
};
